import { useQuery } from '@tanstack/react-query'
import { Card, Title, Text, Flex, Badge } from '@tremor/react'
import { PhoneIcon, PhoneArrowDownLeftIcon, PhoneArrowUpRightIcon } from '@heroicons/react/24/outline'
import { voipApi, type CallTimelineResponse, type CallPeriod } from '@/api/voip'

interface CallTimelineProps {
  extension: string
  hours?: number
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getHourLabels(hours: number): string[] {
  const labels: string[] = []
  const now = new Date()
  for (let i = hours; i >= 0; i--) {
    const time = new Date(now.getTime() - i * 60 * 60 * 1000)
    labels.push(time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }))
  }
  return labels
}

function calculatePosition(startTime: string, endTime: string | null, hoursRange: number): { left: string; width: string } {
  const now = new Date()
  const start = new Date(startTime)
  const end = endTime ? new Date(endTime) : now
  const rangeStart = new Date(now.getTime() - hoursRange * 60 * 60 * 1000)
  const totalMs = hoursRange * 60 * 60 * 1000

  const leftMs = Math.max(0, start.getTime() - rangeStart.getTime())
  const widthMs = Math.min(end.getTime(), now.getTime()) - Math.max(start.getTime(), rangeStart.getTime())

  const leftPercent = (leftMs / totalMs) * 100
  const widthPercent = Math.max(1, (widthMs / totalMs) * 100) // Minimum 1% width to be visible

  return {
    left: `${leftPercent}%`,
    width: `${widthPercent}%`,
  }
}

export function CallTimeline({ extension, hours = 8 }: CallTimelineProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['call-timeline', extension, hours],
    queryFn: () => voipApi.getCallTimeline(extension, hours),
    refetchInterval: 30000, // Refresh every 30s
    enabled: !!extension,
  })

  if (isLoading) {
    return (
      <Card>
        <Flex justifyContent="start" className="gap-2 mb-4">
          <PhoneIcon className="w-5 h-5 text-green-500" />
          <Title>Activité VoIP</Title>
        </Flex>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500" />
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Flex justifyContent="start" className="gap-2 mb-4">
          <PhoneIcon className="w-5 h-5 text-green-500" />
          <Title>Activité VoIP</Title>
        </Flex>
        <Text className="text-gray-500 text-center py-4">
          Impossible de charger l'historique des appels
        </Text>
      </Card>
    )
  }

  const timeline = data as CallTimelineResponse
  const hourLabels = getHourLabels(hours)

  return (
    <Card>
      <Flex justifyContent="between" className="mb-4">
        <Flex justifyContent="start" className="gap-2">
          <PhoneIcon className="w-5 h-5 text-green-500" />
          <Title>Activité VoIP</Title>
        </Flex>
        <Flex className="gap-3">
          <Badge color="blue">
            {timeline.total_calls} appels
          </Badge>
          <Badge color="green">
            {formatDuration(timeline.total_duration_seconds)}
          </Badge>
        </Flex>
      </Flex>

      {/* Timeline visualization */}
      <div className="mt-4">
        {/* Hour labels */}
        <div className="flex justify-between text-xs text-gray-400 mb-1 px-1">
          {hourLabels.filter((_, i) => i % 2 === 0).map((label, idx) => (
            <span key={idx}>{label}</span>
          ))}
        </div>

        {/* Timeline bar */}
        <div className="relative h-10 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
          {/* Grid lines */}
          <div className="absolute inset-0 flex">
            {Array.from({ length: hours }).map((_, i) => (
              <div
                key={i}
                className="flex-1 border-r border-gray-200 dark:border-gray-700"
              />
            ))}
          </div>

          {/* Call periods */}
          {timeline.periods.map((period: CallPeriod, idx: number) => {
            const pos = calculatePosition(period.start_time, period.end_time, hours)
            const isActive = period.is_active
            const isInbound = period.direction === 'inbound'

            return (
              <div
                key={idx}
                className={`absolute top-1 bottom-1 rounded ${
                  isActive
                    ? 'bg-green-500 animate-pulse'
                    : isInbound
                      ? 'bg-blue-500'
                      : 'bg-purple-500'
                }`}
                style={{ left: pos.left, width: pos.width }}
                title={`${formatTime(period.start_time)} - ${period.end_time ? formatTime(period.end_time) : 'En cours'}\n${period.connected || 'Inconnu'}\n${formatDuration(period.duration_seconds || 0)}`}
              />
            )
          })}
        </div>

        {/* Legend */}
        <Flex justifyContent="center" className="gap-4 mt-3">
          <Flex justifyContent="start" className="gap-1">
            <div className="w-3 h-3 rounded bg-blue-500" />
            <Text className="text-xs">Entrant</Text>
          </Flex>
          <Flex justifyContent="start" className="gap-1">
            <div className="w-3 h-3 rounded bg-purple-500" />
            <Text className="text-xs">Sortant</Text>
          </Flex>
          <Flex justifyContent="start" className="gap-1">
            <div className="w-3 h-3 rounded bg-green-500 animate-pulse" />
            <Text className="text-xs">En cours</Text>
          </Flex>
        </Flex>
      </div>

      {/* Call list */}
      {timeline.periods.length > 0 && (
        <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
          {timeline.periods.slice().reverse().map((period: CallPeriod, idx: number) => (
            <Flex
              key={idx}
              justifyContent="between"
              className={`p-2 rounded-lg ${
                period.is_active
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-gray-50 dark:bg-gray-800/50'
              }`}
            >
              <Flex justifyContent="start" className="gap-2">
                {period.direction === 'inbound' ? (
                  <PhoneArrowDownLeftIcon className="w-4 h-4 text-blue-500" />
                ) : (
                  <PhoneArrowUpRightIcon className="w-4 h-4 text-purple-500" />
                )}
                <div>
                  <Text className="font-medium text-sm">
                    {period.connected || 'Numéro inconnu'}
                  </Text>
                  <Text className="text-xs text-gray-500">
                    {formatTime(period.start_time)}
                    {period.end_time && ` - ${formatTime(period.end_time)}`}
                  </Text>
                </div>
              </Flex>
              <Flex justifyContent="end" className="gap-2">
                {period.is_active && (
                  <Badge color="green" size="xs">
                    En appel
                  </Badge>
                )}
                <Text className="text-sm font-medium">
                  {formatDuration(period.duration_seconds || 0)}
                </Text>
              </Flex>
            </Flex>
          ))}
        </div>
      )}

      {timeline.periods.length === 0 && (
        <Text className="text-gray-500 text-center py-4 mt-2">
          Aucun appel dans les {hours} dernières heures
        </Text>
      )}
    </Card>
  )
}
